import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import { usePermissions } from '@/contexts/auth';
import { useCage } from '@/contexts/cage';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';

import { ActiveFishGroupSection } from './ActiveFishGroupSection';
import { SettingsForm } from './SettingsForm';

import styles from './Settings.module.scss';

export default function Settings() {
    const { t } = useTranslation('cageSettings');
    const { cage, cageId, client, location, stock, loading } = useCage();
    const { enabled: fishGroupsEnabled } = usePostHogFeatureFlag('fish-group-tracking');
    const { canAdministerLocation } = usePermissions();

    // The form initialises controlled inputs from these values, so it must not mount until the
    // cage block has resolved — an `undefined` value flips React to an uncontrolled input.
    if (loading || !cage) {
        return <Spinner size="lg" fullScreen />;
    }

    // The same rule the backend applies to the active fish group's endpoints.
    const clientId = client?.id;
    const showFishGroup =
        fishGroupsEnabled &&
        clientId !== undefined &&
        location !== undefined &&
        canAdministerLocation(clientId, location.id);

    return (
        <PageLayout title={cage.name} subtitle={t('settings.pageSubtitle')}>
            <div className={styles.layout}>
                <div className={styles.column}>
                    <SettingsForm
                        cageId={cageId}
                        cageName={cage.name}
                        cageTypeId={cage.cageType?.id}
                        fishTypeId={stock?.fishType?.id}
                        externalId={cage.externalId ?? ''}
                        externalName={cage.externalName ?? ''}
                        locationTypeShortName={location?.locationType?.shortName}
                    />
                </div>
                {showFishGroup && (
                    <div className={styles.column}>
                        <ActiveFishGroupSection cageId={cageId} clientId={clientId} />
                    </div>
                )}
            </div>
        </PageLayout>
    );
}

export { Settings };
